/**
 * SourceDocumentSection — the audit report (or whatever document gave rise
 * to this CAP) sits at the very top of the detail page.
 *
 * Compliance can:
 *   - Upload the source document
 *   - Replace it
 *   - Download what's already attached
 *
 * The text from the document is extracted server-side at upload time and
 * cached on the evidence row, so the "Help me write this" AI helper on the
 * What happened section can read the document directly.
 */

import React, { useState } from 'react';
import SectionCard from './SectionCard';
import Pill from './Pill';
import PhiFriendlyNote from '../../../components/PhiFriendlyNote.jsx';

const API = process.env.REACT_APP_API_URL;

export default function SourceDocumentSection({ data, onRefresh }) {
  const cap = data?.cap;
  const evidence = data?.evidence || [];
  const sourceDoc = evidence.find(
    e => e.id === cap?.source_document_evidence_id || e.kind === 'source'
  );

  const [fileInput, setFileInput] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [showReplace, setShowReplace] = useState(false);

  const upload = async (e) => {
    e.preventDefault();
    if (!fileInput) return;
    setBusy(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', fileInput);
      const res = await fetch(`${API}/api/pcap/caps/${cap.id}/source-document/upload`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      const d = await res.json();
      if (!res.ok || !d.success) throw new Error(d.message || 'Upload failed');
      setFileInput(null);
      setShowReplace(false);
      onRefresh?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <SectionCard
      title="Source document"
      hint="The audit report, finding letter, or any document that started this CAP. Once it's here, the AI helper on What happened can read it directly."
    >
      {error && (
        <div className="mb-3 rounded-lg bg-red-50 ring-1 ring-red-200 p-2.5 text-sm text-red-800">
          {error}
        </div>
      )}

      {sourceDoc && !showReplace && (
        <div className="rounded-xl ring-1 ring-slate-200 bg-gradient-to-br from-slate-50 to-white p-4 flex items-start gap-3">
          <div className="text-2xl leading-none mt-0.5">📄</div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-900 truncate">
              {sourceDoc.file_name || 'Source document'}
            </p>
            <p className="text-[12px] text-slate-500 mt-0.5">
              {sourceDoc.file_mime || 'file'}
              {sourceDoc.file_size && ` · ${Math.ceil(sourceDoc.file_size / 1024)} KB`}
              {sourceDoc.uploaded_at && ` · uploaded ${new Date(sourceDoc.uploaded_at).toLocaleDateString()}`}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Pill tone="bg-emerald-50 text-emerald-800 ring-emerald-200">Attached</Pill>
            <a
              href={`${API}/api/pcap/evidence/${sourceDoc.id}/download`}
              className="text-[12px] text-slate-600 hover:text-slate-900 underline underline-offset-2"
              target="_blank" rel="noreferrer"
            >
              Open
            </a>
            <button
              type="button"
              onClick={() => setShowReplace(true)}
              className="text-[12px] text-slate-600 hover:text-slate-900 underline underline-offset-2"
            >
              Replace
            </button>
          </div>
        </div>
      )}

      {(!sourceDoc || showReplace) && (
        <form onSubmit={upload} className="rounded-xl ring-1 ring-dashed ring-slate-300 bg-slate-50/50 p-4 space-y-3">
          <PhiFriendlyNote variant="upload" />
          <p className="text-sm text-slate-600">
            {sourceDoc
              ? 'Replace the current source document with a new one.'
              : 'Drop in the audit report, finding letter, or whatever document is the source.'}
          </p>
          <input
            type="file"
            onChange={e => setFileInput(e.target.files?.[0] || null)}
            className="block w-full text-sm text-slate-800 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-slate-900 file:text-white file:text-[12px] file:font-medium hover:file:bg-slate-800"
          />
          <p className="text-[12px] text-slate-500">PDF, DOCX, or XLSX. Up to 25 MB. Executables blocked.</p>
          <div className="flex items-center gap-2">
            <button
              type="submit"
              disabled={busy || !fileInput}
              className="px-3.5 py-2 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 transition disabled:opacity-50"
            >
              {busy ? 'Uploading…' : sourceDoc ? 'Replace document' : 'Upload document'}
            </button>
            {showReplace && (
              <button
                type="button"
                onClick={() => { setShowReplace(false); setFileInput(null); setError(null); }}
                className="px-3 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-100"
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      )}
    </SectionCard>
  );
}
